/**
 * Phase 4 gap-close — iOS "Add to Home Screen" prompt.
 *
 * On iOS Safari, web push only works AFTER the user adds the site to
 * their home screen. This component detects iOS Safari that isn't yet
 * running in standalone mode and surfaces a one-time prompt explaining
 * why and how. The user can dismiss it; we remember the dismissal so
 * we don't badger.
 *
 * Mount once at the App root. Renders nothing on Android, on desktop,
 * or in already-installed PWA mode.
 */
import React, { useEffect, useState } from 'react';

const DISMISS_KEY = 'mojo-ios-install-dismissed-at';
const DISMISS_TTL_MS = 30 * 24 * 3600_000;   // 30 days — re-ask later

function _isIosSafari() {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iPad|iPhone|iPod/.test(ua);
  const webkit = /WebKit/i.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/i.test(ua);
  return iOS && webkit;
}

function _isStandalone() {
  if (typeof window === 'undefined') return false;
  // navigator.standalone is iOS-Safari-specific; matchMedia is the cross-
  // platform tell for "added to home screen / installed PWA".
  if (window.navigator && window.navigator.standalone === true) return true;
  try {
    return window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  } catch (_) {
    return false;
  }
}

function _wasRecentlyDismissed() {
  try {
    const t = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (!t) return false;
    return (Date.now() - t) < DISMISS_TTL_MS;
  } catch (_) {
    return false;
  }
}

export default function IosInstallPrompt() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!_isIosSafari()) return;
    if (_isStandalone()) return;
    if (_wasRecentlyDismissed()) return;
    // Delay a few seconds so we don't jump into the user's face on the
    // very first frame of a landing page.
    const t = setTimeout(() => setVisible(true), 3500);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch (_) {}
    setVisible(false);
  };

  return (
    <div
      data-testid="ios-install-prompt"
      role="dialog"
      aria-label="Add Mojo Money to your home screen"
      style={{
        position:    'fixed',
        left:        12, right: 12, bottom: 12,
        zIndex:      9998,
        background:  '#1B0C34',
        color:       '#fff',
        padding:     '12px 16px',
        borderRadius: 12,
        boxShadow:   '0 4px 14px rgba(0,0,0,0.3)',
        fontSize:    14,
        lineHeight:  1.4,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        Add Mojo Money to your Home Screen
      </div>
      <div>
        For faster access, secure notifications, and to receive payment alerts
        on iOS, tap{' '}
        <span style={{ background: '#f2b10c', color: '#1B0C34', padding: '0 6px',
                        borderRadius: 4, fontWeight: 600 }}>Share</span>
        {' '}then{' '}
        <span style={{ background: '#f2b10c', color: '#1B0C34', padding: '0 6px',
                        borderRadius: 4, fontWeight: 600 }}>Add to Home Screen</span>.
        Installing protects you from look-alike sites — the app icon proves
        the page is the real Mojo Money.
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
        <button
          onClick={dismiss}
          data-testid="ios-install-dismiss-btn"
          style={{ background: 'transparent', color: '#fff',
                   border: '1px solid #ffffff80',
                   padding: '4px 12px', borderRadius: 6,
                   fontSize: 13, cursor: 'pointer' }}
        >Got it</button>
      </div>
    </div>
  );
}
